import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  CreateButton,
  EditButton,
  List,
  RecordContextProvider,
  ReferenceField,
  ShowButton,
  TextField,
  TopToolbar,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { dataProvider } from 'src/providers/data-provider';
import { JsonViewField } from '../logs/JsonViewField';
import { AnalysisQueueImage } from './DocumentShow';

const ActionButtons = ({ record }: { record: any }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleDelete = async () => {
    if (window.confirm(translate('ra.message.are_you_sure'))) {
      try {
        const gqlQuery = `
          mutation request_delete_form_submission($params: request_delete_form_submission_input!) {
            request_delete_form_submission(params: $params) {
              error
            }
          }
        `;

        const variables = {
          params: {
            form_submission_ids: [record.id],
          },
        };

        const response = await dataProvider.create('form_submission', {
          data: {},
          meta: {
            gqlQuery,
            variables,
          },
        });

        if (response.data?.error) {
          throw new Error(response.data.error);
        }

        notify(translate('form_submission.notification.deleted'), { type: 'success' });
        refresh();
      } catch (error) {
        console.error('Delete error:', error);
        notify(translate('form_submission.notification.deleted_error'), { type: 'error' });
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <ShowButton
        resource="form_submission"
        record={record}
        variant="outlined"
        label={translate('form_submission.actions.view_detail')}
      />
      <EditButton
        resource="form_submission"
        record={record}
        variant="outlined"
        label={translate('form_submission.actions.edit')}
      />
      <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete} size="small">
        {translate('form_submission.actions.delete')}
      </Button>
    </Box>
  );
};

const DocumentCard = ({ record }: { record: any }) => (
  <RecordContextProvider value={record}>
    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: '0px' }}>
      <CardContent sx={{ p: 0 }}>
        <Grid container spacing={0}>
          <Grid
            item
            xs={1}
            sx={{
              borderRight: '1px solid #e0e0e0',
              p: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {record.id}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            sx={{
              borderRight: '1px solid #e0e0e0',
              p: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ReferenceField source="form_template_id" reference="form_template">
              <TextField source="name" variant="h6" component="div" />
            </ReferenceField>
          </Grid>
          <Grid
            item
            xs={2}
            sx={{
              borderRight: '1px solid #e0e0e0',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AnalysisQueueImage />
          </Grid>
          <Grid
            item
            xs={5}
            sx={{
              borderRight: '1px solid #e0e0e0',
              p: 2,
              overflow: 'auto',
              maxHeight: '200px',
            }}
          >
            <JsonViewField fieldKey="expected_data" />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ActionButtons record={record} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </RecordContextProvider>
);

const DocumentListActions = () => (
  <TopToolbar>
    {/* <FilterButton /> */}
    <CreateButton />
    {/* <ExportButton /> */}
  </TopToolbar>
);

const DocumentListView = () => {
  const { data } = useListContext();
  const translate = useTranslate();

  return (
    <Box>
      {/* Header */}
      <Card sx={{ backgroundColor: '#f5f5f5', borderRadius: '0px' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                {translate('form_submission.fields.id')}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" fontWeight="bold">
                {translate('form_submission.fields.template_name')}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" fontWeight="bold">
                {translate('form_submission.fields.image')}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography variant="subtitle2" fontWeight="bold">
                {translate('form_submission.fields.submitted_data')}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="subtitle2" fontWeight="bold">
                {translate('form_submission.fields.actions')}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data rows */}
      {data?.map((record: any) => (
        <DocumentCard key={record.id} record={record} />
      ))}
    </Box>
  );
};

export const DocumentList = () => {
  return (
    <List
      title="書類一覧"
      actions={<DocumentListActions />}
      filters={[]}
      perPage={10}
      sort={{ field: 'id', order: 'DESC' }}
      filterDefaultValues={{ 'form_template_id@_is_null': false, is_admin_created: true, 'deleted_at@_is_null': true }}
    >
      <DocumentListView />
    </List>
  );
};

export default DocumentList;
